import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
// Main Navigation
import LoginPage from '@/views/LoginPage.vue'
import SsoCallback from '@/views/SsoCallback.vue'
import RequestView from '@/views/request/RequestView.vue'
import CustomerView from '@/views/customer/CustomerView.vue'

// Sub Navigation 
//Request
import BoardPage from '@/views/request/BoardPage.vue'
import ManagePage from '@/views/request/ManagePage.vue'
import AddPage from '@/views/request/AddPage.vue'
import ModifyPage from '@/views/request/ModifyPage.vue'
import ViewPage from '@/views/request/ViewPage.vue'
import ViewStatPage from '@/views/request/ViewStatPage.vue'
import PlanListPage from '@/views/request/PlanListPage.vue'
import PaymentListPage from '@/views/request/PaymentListPage.vue'

// Custommer
import CustomerManage from '@/views/customer/CustomerManage.vue'
import CustomerAdd from '@/views/customer/CustomerAdd.vue'

import { checkAuth } from '@/utils/auth'
import { usePlanStore } from '@/stores/plan';
import { useRequestStore } from '@/stores/request'
import { ElMessage, ElNotification, ElMessageBox } from 'element-plus'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'Main',
    redirect: '/request',
  },
  {
    path: '/login',
    name: 'Login',
    component: LoginPage,
    meta: { requiresAuth: false }
  },
  {
    path: '/sso',
    name: 'SsoCallback',
    component: SsoCallback,
    meta: { requiresAuth: false }
  },
  {
    path: '/request',
    component: RequestView,
    children: [
      {
        path: '',
        name: 'Request',
        redirect: '/request/board'
      },
      {
        path: 'board',
        name: 'Board',
        component: BoardPage,
        meta: { requiresAuth: true }
      },
      {
        path: 'add',
        name: 'Add',
        component: AddPage,
        meta: { requiresAuth: true }
      },
      {
        path: 'modify/:id',
        name: 'Modify',
        component: ModifyPage,
        props: true,
        meta: { requiresAuth: true }
      },
      {
        path: 'view/:id',
        name: 'View',
        component: ViewPage,
        props: true,
        meta: { requiresAuth: true }
      },
      {
        path: 'viewStat/:id/:stat',
        name: 'ViewStat',
        component: ViewStatPage,
        props: true,
        meta: { requiresAuth: true }
      },
      {
        path: 'manage',
        name: 'Manage',
        component: ManagePage,
        meta: { requiresAuth: true }
      },
      {
        path: 'plan',
        name: 'PlanList',
        component: PlanListPage,
        meta: { requiresAuth: true },
        props: route => ({ param: route.query.param || '' })
      },
      {
        path: 'payment',
        name: 'PaymentList',
        component: PaymentListPage,
        meta: { requiresAuth: true },
        props: route => ({ param: route.query.param || '' })
      },
    ]
  },
  {
    path: '/customer',
    component: CustomerView,
    children: [
      {
        path: '',
        name: 'Customer',
        redirect: '/customer/manage'
      },
      {
        path: 'manage',
        name: 'CustomerManage',
        component: CustomerManage,
        meta: { requiresAuth: true }
      },
      {
        path: 'add',
        name: 'CustomerAdd',
        component: CustomerAdd,
        meta: { requiresAuth: true }
      },
      {
        path: 'modify/:id',
        name: 'CustomerModify',
        component: () => import('@/views/customer/CustomerModify.vue'),
        props: true,
        meta: { requiresAuth: true }
      },
      {
        path: 'view/:id',
        name: 'CustomerDetail',
        component: () => import('@/views/customer/CustomerDetail.vue'),
        props: true,
        meta: { requiresAuth: true }
      },
    ]
  },
  // {
  //   path: '/:pathMatch(.*)*',
  //   name: '404-not-found',
  //   redirect: '/request'
  // },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

const getAllRouteNames = (routes: Array<RouteRecordRaw>): string[] => {
  const names: string[] = [];
  
  routes.forEach(route => {
    if (route.name && typeof route.name === 'string') {
      names.push(route.name);
    }
    
    if (route.children) {
      names.push(...getAllRouteNames(route.children));
    }
  });
  
  return names;
};



const validateDynamicRoute = (to: any): boolean => {
  const dynamicRoutes = {
    ViewStat: ['id', 'stat'],
    View: ['id'],
    Modify: ['id'],
  };

  const requiredParams = dynamicRoutes[to.name as keyof typeof dynamicRoutes];
  if (!requiredParams) return true;

  return requiredParams.every(param => to.params[param]);
};

router.beforeEach(async (to, from, next) => {
  const planStore = usePlanStore();

  try {
    const userData = await checkAuth();

    // SSO 콜백은 컴포넌트에서 토큰을 저장하므로 인증 검사를 건너뛴다 (hash 유실 방지)
    if (to.name === 'SsoCallback') {
      return next();
    }

    if (!userData && to.name !== 'Login') {
      // console.log('Redirecting to Login');
      return next({ name: 'Login' });
    }

    if (userData && to.name === 'Login') {
      // console.log('Authenticated user, redirecting to Home');
      return next({ path: '/' });
    }

    if (userData && planStore.beusPlanData.length === 0 && planStore.baPlanData.length === 0) {
      console.log('Fetching plans...');
      await planStore.fetchPlans();
    }

    const isRefresh = !from.name;

    if (!isRefresh) {
      if ((to.name === 'ViewStat' && from.name !== 'Board') && (to.name === 'ViewStat' && from.name !== 'Modify') && (to.name === 'ViewStat' && from.name !== 'PlanList') && (to.name === 'ViewStat' && from.name !== 'PaymentList')) {
        console.log(`1. from ${from.name as string} to ${to.name}`);
        ElMessage.error('잘못된 접근입니다. 대시보드로 이동합니다.');
        return next({ name: 'Board' });
      }

      if ((to.name === 'View' && from.name !== 'Manage') && (to.name === 'View' && from.name !== 'Modify') && (to.name === 'View' && from.name !== 'PlanList') && (to.name === 'View' && from.name !== 'PaymentList')) {
        console.log(`2. from ${from.name as string} to ${to.name}`);
        ElMessage.error('잘못된 접근입니다. 리퀘스트 관리 페이지로 이동합니다.');
        return next({ name: 'Manage' });
      }

      if (to.name === 'Modify' && !['ViewStat', 'View'].includes(from.name as string)) {
        console.log(`3. from ${from.name as string} to ${to.name}`);
        ElMessage.error('잘못된 접근입니다. 대시보드로 이동합니다.');
        return next({ name: 'Board' });
      }

      if (['ViewStat', 'View'].includes(from.name as string) && to.name !== from.name && to.name !== 'Modify') {
        console.log(`4. from ${from.name as string} to ${to.name as string}`);
        useRequestStore().clearRequestData();
      }
    }

    const routeNames = getAllRouteNames(routes);
    if (to.name && (!routeNames.includes(to.name.toString()) || !validateDynamicRoute(to))) {
      ElMessageBox.alert('존재하지 않거나 잘못된 경로입니다. 대시보드로 이동합니다.', {
        confirmButtonText: '확인',
        callback: () => {
          router.push("/request");
        },
      });
    }

    const param = from.query.param;
    const userParamRoutes = ['PlanList', 'PaymentList'];
    if (param && !to.query.param && userParamRoutes.includes(from.name as string) && userParamRoutes.includes(to.name as string)) {
      next({ ...to, query: { ...to.query, param } });
    }
    
    // 기본적으로 요청된 경로로 이동
    // console.log('Proceeding to:', to.path);
    next();

  } catch (error) {
    console.error('Authentication error:', error);
    next({ name: 'Login' });
  }
});
export default router
